(function (global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.Carousel = global.NewHomeSource.Carousel || {};

  const Carousel = global.NewHomeSource.Carousel;

  if (!global.NewHomeSource.Helpers ||
      !global.NewHomeSource.Helpers.Array ||
      !Carousel.Limit) {
    return;
  }

  const arrayHelper = global.NewHomeSource.Helpers.Array;

  class ScrollContain {
    /**
     * Initializes a new instance of the class.
     * Sets the initial state to null.
     */
    constructor() {
      this.state = null;
      this.pixelTolerance = 1;
    }

    /**
     * Initializes the carousel scroll container with the given state.
     * Sets up the scroll bounds based on the content and view sizes.
     *
     * @param {Object} state - The state object containing carousel properties.
     * @param {Function} state.get - Function to retrieve state properties by key.
     * @returns {this} The current instance for chaining.
     */
    initialize(state) {
      this.state = state;
      const contentSize = this.state.get('contentSize');
      const viewSize = this.state.get('viewSize');
      this.scrollBounds = new Carousel.Limit();
      this.scrollBounds.initialize(-contentSize + viewSize, 0);
      return this;
    }

    /**
     * Calculates and returns the scroll containment limit for the carousel.
     *
     * This method determines the minimum and maximum indices for the scrollable area
     * based on the bounded snap points. It uses the first and last snap points to
     * compute the containment range, then initializes and returns a `Carousel.Limit`
     * object with these values.
     *
     * @returns {Carousel.Limit} The initialized limit object representing the scroll containment range.
     */
    findScrollContainLimit() {
      const snapsBounded = this.measureBounded();
      const startSnap = snapsBounded[0];
      const endSnap = arrayHelper.arrayLast(snapsBounded);
      const min = snapsBounded.lastIndexOf(startSnap);
      const max = snapsBounded.indexOf(endSnap) + 1;
      const limit = new Carousel.Limit();
      limit.initialize(min, max);
      return limit;
    }

    /**
     * Calculates and returns an array of bounded scroll positions for each snap point.
     * For each snap point, it determines whether to use the minimum or maximum scroll bound,
     * or to constrain the snap position within the scroll bounds, applying pixel tolerance checks.
     * The resulting scroll bounds are rounded to three decimal places.
     *
     * @returns {number[]} An array of scroll positions, each rounded to three decimal places.
     */
    measureBounded() {
      const snaps = this.state.get('snaps');
      return snaps.map((snapAligned, index) => {
        const { min, max } = this.scrollBounds;
        const snap = this.scrollBounds.constrain(snapAligned);
        const isFirst = !index;
        const isLast = arrayHelper.arrayLastIndex(snaps) === index;

        if (isFirst) {
          return max;
        }

        if (isLast) {
          return min;
        }

        if (this.usePixelTolerance(min, snap)) {
          return min;
        }

        if (this.usePixelTolerance(max, snap)) {
          return max;
        }

        return snap;
      }).map((scrollBound) => parseFloat(scrollBound.toFixed(3)));
    }

    /**
     * Calculates and returns the contained scroll snap points based on the current state.
     *
     * If the content size is less than or equal to the view size plus a pixel tolerance,
     * returns an array containing only the maximum scroll bound. Otherwise, returns a
     * sliced array of bounded snap points within the scroll containment limits.
     *
     * @returns {number[]} An array of scroll snap points that are contained within the view.
     */
    measureContained() {
      const contentSize = this.state.get('contentSize');
      const viewSize = this.state.get('viewSize');
      const pixelTolerance = this.state.get('pixelTolerance');
      const snapsBounded = this.measureBounded();

      if (contentSize <= viewSize + pixelTolerance) {
        return [this.scrollBounds.max];
      }

      const scrollContainLimit = this.findScrollContainLimit();
      return snapsBounded.slice(scrollContainLimit.min, scrollContainLimit.max);
    }

    /**
     * Determines if the difference between the given bound and snap values
     * is within the configured pixel tolerance.
     *
     * @param {number} bound - The current boundary value.
     * @param {number} snap - The target snap value to compare against.
     * @returns {boolean} True if the absolute difference is less than or equal to pixelTolerance, otherwise false.
     */
    usePixelTolerance(bound, snap) {
      return Math.abs(bound - snap) <= this.pixelTolerance;
    }
  }

  global.NewHomeSource.Carousel.ScrollContain = ScrollContain;
}(window));
